import React, { useState, useEffect, useRef } from 'react';
import Loader from '../../components/Loader';
import Calling from '../../api/Calling';
import { toast } from 'react-toastify';
import formatDate from '../../components/formatDate';
import { FaArrowLeft, FaArrowRight, FaSortAmountDownAlt, FaSortAmountUp, FaTrash, FaTimes, FaUser, FaCalendarAlt, FaMoneyBillWave, FaClock, FaExternalLinkAlt, FaCopy, FaReceipt, FaCheckCircle, FaTimesCircle, FaBan } from 'react-icons/fa';
import generateCaptcha from '../../components/captcha';
import { useNavigate } from 'react-router-dom';
import MainButton from '../../components/ul/MainButton';
import { IoMdSearch } from 'react-icons/io';
import DeleteButton from '../../components/ul/DeleteButton';
import { MdDeleteSweep } from 'react-icons/md';
import { LuDot } from 'react-icons/lu';
import NotFound from '../../components/ul/NotFound';
import getISTDate from '../../components/getISTDate';
import { TbReceipt } from 'react-icons/tb';

const PlanHistory = () => {
    const [loader, setLoader] = useState(false);
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedPlans, setSelectedPlans] = useState([]);
    const [data, setData] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [sortOrder, setSortOrder] = useState(-1);
    const [selectAll, setSelectAll] = useState(false);
    const [startDate, setStartDate] = useState(getISTDate());
    const [endDate, setEndDate] = useState(getISTDate());
    const [totalRecords, setTotalRecords] = useState(0);
    const [activeCount, setActiveCount] = useState(0);
    const [expiredCount, setExpiredCount] = useState(0);
    const [cancelledCount, setCancelledCount] = useState(0);
    const [statusFilter, setStatusFilter] = useState('');
    const [selectState, setSelectState] = useState('today');
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const limit = 20;
    const navigate = useNavigate();
    const modalRef = useRef(null);

    // Fetch data from API
    const fetchData = async () => {
        setLoader(true);
        try {
            const response = await Calling.getPlanHistory(
                currentPage,
                limit,
                startDate,
                endDate,
                '',
                statusFilter,
                sortOrder,
                search
            );
            if (response?.status) {
                setData(response?.data || []);
                setTotalPages(response?.pagination?.totalPages || 1);
                setTotalRecords(response?.pagination?.total || 0);
                // Set status counts from API response
                setActiveCount(response?.statusCounts?.active || 0);
                setExpiredCount(response?.statusCounts?.expired || 0);
                setCancelledCount(response?.statusCounts?.cancelled || 0);
            } else {
                toast.error(response?.message);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to fetch plan history');
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line
    }, [currentPage, sortOrder, startDate, endDate, statusFilter, selectState]);

    // Close modal when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                setShowModal(false);
                setSelectedPlan(null);
            }
        };

        if (showModal) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showModal]);

    // Close modal on ESC key
    useEffect(() => {
        const handleEscKey = (event) => {
            if (event.key === 'Escape') {
                setShowModal(false);
                setSelectedPlan(null);
            }
        };

        if (showModal) {
            document.addEventListener('keydown', handleEscKey);
        }

        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [showModal]);

    const handleSelectAll = () => {
        if (!selectAll) {
            const allPlanIds = data.map((plan) => plan._id);
            setSelectedPlans(allPlanIds);
        } else {
            setSelectedPlans([]);
        }
        setSelectAll(!selectAll);
    };

    const handleSelectPlan = (id) => {
        if (selectedPlans.includes(id)) {
            setSelectedPlans(selectedPlans.filter((planId) => planId !== id));
        } else {
            setSelectedPlans([...selectedPlans, id]);
        }
    };

    const handleDateRangeFilter = (range) => {
        const todayUTC = new Date();

        const toIST = (date) => {
            const istOffset = 5.5 * 60 * 60 * 1000;
            const istDate = new Date(date.getTime() + istOffset);
            return istDate.toISOString().split('T')[0];
        };

        let start, end;

        switch (range) {
            case 'today':
                start = toIST(todayUTC);
                end = start;
                setSelectState('today');
                break;

            case 'yesterday':
                const yesterdayUTC = new Date(todayUTC);
                yesterdayUTC.setDate(todayUTC.getDate() - 1);
                start = toIST(yesterdayUTC);
                end = start;
                setSelectState('yesterday');
                break;

            case 'thisWeek':
                const firstDayOfWeekUTC = new Date(todayUTC);
                firstDayOfWeekUTC.setDate(todayUTC.getDate() - todayUTC.getDay());
                const lastDayOfWeekUTC = new Date(firstDayOfWeekUTC);
                lastDayOfWeekUTC.setDate(firstDayOfWeekUTC.getDate() + 6);
                start = toIST(firstDayOfWeekUTC);
                end = toIST(lastDayOfWeekUTC);
                setSelectState('thisWeek');
                break;

            case 'thisMonth':
                const firstDayOfMonthUTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth(), 1);
                const lastDayOfMonthUTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() + 1, 0);
                start = toIST(firstDayOfMonthUTC);
                end = toIST(lastDayOfMonthUTC);
                setSelectState('thisMonth');
                break;

            case 'last6Months':
                const firstDayLast6UTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() - 5, 1);
                const lastDayLast6UTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() + 1, 0);
                start = toIST(firstDayLast6UTC);
                end = toIST(lastDayLast6UTC);
                setSelectState('last6Months');
                break;

            case 'thisYear':
                const firstDayOfYearUTC = new Date(todayUTC.getFullYear(), 0, 1);
                const lastDayOfYearUTC = new Date(todayUTC.getFullYear(), 11, 31);
                start = toIST(firstDayOfYearUTC);
                end = toIST(lastDayOfYearUTC);
                setSelectState('thisYear');
                break;

            default:
                start = '';
                end = '';
                setSelectState('range');
        }

        setStartDate(start);
        setEndDate(end);
        setCurrentPage(1);
    };

    const handleDeleteSelected = async (id) => {
        const content = `You are about to delete plan history. To confirm, please solve this math problem.`;
        const check = generateCaptcha(content);
        if (check) {
            try {
                setLoader(true);
                if (id) {
                    // Single delete
                    const response = await Calling.detelePurchasePlan(id);
                    if (response?.status) {
                        toast.success(response?.message);
                        setSelectedPlans([]);
                        setSelectAll(false);
                        fetchData();
                    } else {
                        toast.error(response?.message);
                    }
                } else {
                    // Bulk delete - delete one by one
                    let successCount = 0;
                    let failCount = 0;
                    for (const planId of selectedPlans) {
                        const response = await Calling.detelePurchasePlan(planId);
                        if (response?.status) {
                            successCount++;
                        } else {
                            failCount++;
                        }
                    }
                    if (successCount > 0) {
                        toast.success(`Successfully deleted ${successCount} plan(s)`);
                    }
                    if (failCount > 0) {
                        toast.error(`Failed to delete ${failCount} plan(s)`);
                    }
                    setSelectedPlans([]);
                    setSelectAll(false);
                    fetchData();
                }
            } catch (error) {
                console.error('Error deleting plans:', error);
                toast.error('Failed to delete plan(s)');
            } finally {
                setLoader(false);
            }
        } else {
            toast.error("Captcha answer is incorrect", { autoClose: 2000 });
        }
    };

    const handleUserClick = (userId) => {
        if (userId) {
            window.open(`/manage/user/all/info/${userId}`, '_blank');
        }
    };

    const handleRowClick = (plan) => {
        setSelectedPlan(plan);
        setShowModal(true);
    };

    const handleReportSort = () => {
        setSelectState('sort');
        setSortOrder(sortOrder === 1 ? -1 : 1);
    };

    const handleStatusFilter = (status) => {
        setStatusFilter(status === statusFilter ? '' : status);
        setSelectState('status');
        setCurrentPage(1);
    };

    const handleFormDate = (e) => {
        if (e.target.name === "startDate") {
            setStartDate(e.target.value);
            setSelectState('range');
        } else {
            setEndDate(e.target.value);
            setSelectState('range');
        }
    };

    const handleSearchPlan = (e) => {
        setCurrentPage(1);
        setSearch(e.target.value);
        setSelectState('search');
    };

    useEffect(() => {
        if (search.trim() !== '') {
            const delayDebounce = setTimeout(() => {
                fetchData();
            }, 500);
            return () => clearTimeout(delayDebounce);
        }
    }, [search]);

    const getStatusBadge = (status) => {
        switch (status) {
            case 'active':
                return <span className='px-3 py-1 w-max text-sm rounded-full bg-green-100 text-green-600 flex items-center'><LuDot size={20} /> Active</span>;
            case 'expired':
                return <span className='px-3 py-1 w-max text-sm rounded-full bg-yellow-100 text-yellow-600 flex items-center'><LuDot size={20} /> Expired</span>;
            case 'cancelled':
                return <span className='px-3 py-1 w-max text-sm rounded-full bg-red-100 text-red-600 flex items-center'><LuDot size={20} /> Cancelled</span>;
            default:
                return <span className='px-3 py-1 w-max text-sm rounded-full bg-gray-100 text-gray-600 flex items-center'><LuDot size={20} /> {status}</span>;
        }
    };

    const handleCopyTrn = async (trn) => {
        if (!trn) {
            toast.error('No transaction ID to copy');
            return;
        }
        try {
            await navigator.clipboard.writeText(trn);
            toast.success('Transaction ID copied!', { autoClose: 1500 });
        } catch (err) {
            const textArea = document.createElement('textarea');
            textArea.value = trn;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            toast.success('Transaction ID copied!', { autoClose: 1500 });
        }
    };

    // Stats Card Component
    const StatsCard = ({ title, count, icon: Icon, bgColor, iconBgColor, textColor, borderColor, onClick, isActive }) => (
        <div
            onClick={onClick}
            className={`p-4 rounded-xl border transition-all cursor-pointer hover:shadow-md ${isActive
                ? `${borderColor} ring-2 ring-offset-1`
                : 'border-main-border hover:border-main-blue/30'
                } ${bgColor}`}
        >
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-sm text-main-subfont">{title}</p>
                    <p className={`text-2xl font-bold mt-1 ${textColor}`}>
                        {loader ? (
                            <span className="flex items-center text-main-subfont text-base">
                                <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                ...
                            </span>
                        ) : (
                            count.toLocaleString()
                        )}
                    </p>
                </div>
                <div className={`p-3 rounded-xl ${iconBgColor}`}>
                    <Icon size={24} className={textColor} />
                </div>
            </div>
        </div>
    );

    return (
        <div className="w-full min-h-screen text-main-font">
            {/* Page Header */}
            <div className="mb-6">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-main-blue text-main-white">
                        <TbReceipt size={24} />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-main-font">Plan History</h1>
                        <p className="text-sm text-main-subfont">View and manage all plan purchases</p>
                    </div>
                </div>
            </div>

            {/* Main Card */}
            <div className="bg-main-white rounded-2xl shadow-sm border border-main-border overflow-hidden">
                {/* Stats Cards Grid */}
                <div className="p-4">
                    <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-3 gap-4">
                        {/* Total Plans Card */}
                        <StatsCard
                            title="Total Plans"
                            count={totalRecords}
                            icon={TbReceipt}
                            bgColor="bg-main-th"
                            iconBgColor="bg-main-exBlue/10"
                            textColor="text-main-exBlue"
                            borderColor="border-main-exBlue ring-main-exBlue/30"
                            onClick={() => {
                                setStatusFilter('');
                                setSelectState('today');
                                setCurrentPage(1);
                            }}
                            isActive={statusFilter === ''}
                        />

                        {/* Active Plans Card */}
                        <StatsCard
                            title="Active Plans"
                            count={activeCount}
                            icon={FaCheckCircle}
                            bgColor="bg-green-50"
                            iconBgColor="bg-green-100"
                            textColor="text-green-600"
                            borderColor="border-green-500 ring-green-300"
                            onClick={() => handleStatusFilter('active')}
                            isActive={statusFilter === 'active'}
                        />

                        {/* Expired Plans Card */}
                        <StatsCard
                            title="Expired Plans"
                            count={expiredCount}
                            icon={FaTimesCircle}
                            bgColor="bg-yellow-50"
                            iconBgColor="bg-yellow-100"
                            textColor="text-yellow-600"
                            borderColor="border-yellow-500 ring-yellow-300"
                            onClick={() => handleStatusFilter('expired')}
                            isActive={statusFilter === 'expired'}
                        />

                        {/* Cancelled Plans Card */}
                        {/* <StatsCard
                            title="Cancelled Plans"
                            count={cancelledCount}
                            icon={FaBan}
                            bgColor="bg-red-50"
                            iconBgColor="bg-red-100"
                            textColor="text-red-600"
                            borderColor="border-red-500 ring-red-300"
                            onClick={() => handleStatusFilter('cancelled')}
                            isActive={statusFilter === 'cancelled'}
                        /> */}
                    </div>
                </div>

                {/* Date Range Filters */}
                <div className="px-4 py-3 border-t border-main-border">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        {/* Date Range Buttons */}
                        <div className="flex flex-wrap gap-2">
                            <MainButton onClick={() => handleDateRangeFilter('today')} label={'Today'} selectState={selectState === 'today'} />
                            <MainButton onClick={() => handleDateRangeFilter('yesterday')} label={'Yesterday'} selectState={selectState === "yesterday"} />
                            <MainButton onClick={() => handleDateRangeFilter('thisWeek')} label={'This Week'} selectState={selectState === 'thisWeek'} />
                            <MainButton onClick={() => handleDateRangeFilter('thisMonth')} label={'This Month'} selectState={selectState === 'thisMonth'} />
                            <MainButton onClick={() => handleDateRangeFilter('last6Months')} label={'Last 6 Months'} selectState={selectState === 'last6Months'} />
                            <MainButton onClick={() => handleDateRangeFilter('thisYear')} label={'This Year'} selectState={selectState === 'thisYear'} />
                        </div>

                        {/* Date Inputs */}
                        <div className="flex flex-col sm:flex-row gap-2">
                            <input
                                type="date"
                                value={startDate}
                                name="startDate"
                                onChange={handleFormDate}
                                className={`px-4 py-2 rounded-xl border cursor-pointer transition-all ${selectState === "range"
                                    ? 'border-main-blue text-main-blue bg-main-th'
                                    : 'border-main-border text-main-subfont bg-main-white'
                                    } focus:border-main-blue focus:outline-none`}
                            />
                            <input
                                type="date"
                                value={endDate}
                                name="endDate"
                                onChange={handleFormDate}
                                className={`px-4 py-2 rounded-xl border cursor-pointer transition-all ${selectState === "range"
                                    ? 'border-main-blue text-main-blue bg-main-th'
                                    : 'border-main-border text-main-subfont bg-main-white'
                                    } focus:border-main-blue focus:outline-none`}
                            />
                        </div>
                    </div>
                </div>

                {/* Action Buttons and Search */}
                <div className="px-4 py-3 border-t border-main-border">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        {/* Sort Button */}
                        <div className="flex flex-wrap gap-2">
                            <MainButton
                                onClick={handleReportSort}
                                label={
                                    <span className="flex items-center gap-2">
                                        {sortOrder === -1 ? <FaSortAmountDownAlt /> : <FaSortAmountUp />}
                                    </span>
                                }
                                selectState={selectState === 'sort'}
                            />
                        </div>

                        {/* Search Input */}
                        <div className="relative w-full lg:w-80">
                            <IoMdSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-main-subfont" size={20} />
                            <input
                                type="search"
                                className="w-full pl-11 pr-4 py-2.5 border border-main-border rounded-xl bg-main-bg focus:bg-main-white focus:ring-2 focus:ring-main-blue/20 focus:border-main-blue focus:outline-none transition-all"
                                value={search}
                                onChange={handleSearchPlan}
                                placeholder="Search by Transaction ID..."
                            />
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-main-th">
                                <th className="ps-4 py-4 text-left">
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="checkbox"
                                            className="w-4 h-4 rounded border-main-border text-main-blue focus:ring-main-blue cursor-pointer"
                                            checked={selectAll}
                                            onChange={handleSelectAll}
                                        />
                                        {selectedPlans.length > 0 && (
                                            <DeleteButton
                                                onClick={() => handleDeleteSelected(null)}
                                                label={<MdDeleteSweep size={20} />}
                                            />
                                        )}
                                    </div>
                                </th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">#</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">User</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Plan Name</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Price</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Transaction</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Status</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Date</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>

                        {loader ? (
                            <tbody>
                                <tr>
                                    <td colSpan={9} className="text-center py-12">
                                        <Loader />
                                    </td>
                                </tr>
                            </tbody>
                        ) : (
                            <tbody className="divide-y divide-main-border">
                                {data.length > 0 ? data.map((plan, index) => (
                                    <tr key={plan._id} className="hover:bg-main-thHover transition-colors cursor-pointer">
                                        <td className="ps-4 py-4" onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                checked={selectedPlans.includes(plan._id)}
                                                onChange={() => handleSelectPlan(plan._id)}
                                                className="w-4 h-4 rounded border-main-border text-main-blue focus:ring-main-blue cursor-pointer"
                                            />
                                        </td>
                                        <td className="px-4 py-4 text-sm text-main-subfont" onClick={() => handleRowClick(plan)}>
                                            {index + 1 + (currentPage - 1) * limit}
                                        </td>
                                        <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                                            <div
                                                className="flex items-center gap-2 cursor-pointer group"
                                                onClick={() => handleUserClick(plan?.u_id?._id)}
                                            >
                                                <div className="w-8 h-8 rounded-lg bg-main-blue text-main-white flex items-center justify-center font-bold text-sm">
                                                    {plan?.u_id?.u_name?.charAt(0).toUpperCase() || 'U'}
                                                </div>
                                                <span className="text-main-blue group-hover:underline font-medium flex items-center gap-1">
                                                    {plan?.u_id?.u_name || 'N/A'}
                                                    <FaExternalLinkAlt size={10} />
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4 font-medium text-main-font" onClick={() => handleRowClick(plan)}>
                                            {plan.p_name || 'N/A'}
                                        </td>
                                        <td className="px-4 py-4" onClick={() => handleRowClick(plan)}>
                                            <span className="text-main-blue font-bold">₹{plan.p_price}</span>
                                        </td>
                                        <td className="px-4 py-4" onClick={() => handleRowClick(plan)}>
                                            <span className="text-xs bg-main-th px-2.5 py-1.5 rounded-lg font-mono text-main-subfont border border-main-border">
                                                {plan.p_trn || 'N/A'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4" onClick={() => handleRowClick(plan)}>
                                            {getStatusBadge(plan.status)}
                                        </td>
                                        <td className="px-4 py-4 text-sm text-main-subfont" onClick={() => handleRowClick(plan)}>
                                            {formatDate(plan.createdAt)}
                                        </td>
                                        <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                                            <button
                                                onClick={() => handleDeleteSelected(plan._id)}
                                                className="p-2.5 text-main-delete hover:bg-main-delete/10 rounded-xl transition-colors"
                                                title="Delete"
                                            >
                                                <FaTrash size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan={9} className="py-12">
                                            <NotFound />
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        )}
                    </table>
                </div>

                {/* Pagination */}
                <div className="p-6 border-t border-main-border">
                    <div className="flex flex-col md:flex-row justify-center items-center relative">
                        <div className="flex gap-3 mb-4 md:mb-0">
                            <button
                                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${currentPage === 1
                                    ? "bg-main-bg border border-main-border text-main-subfont cursor-not-allowed"
                                    : "bg-main-blue text-main-white hover:bg-main-blue/90"
                                    }`}
                            >
                                <FaArrowLeft size={12} /> Previous
                            </button>
                            <button
                                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages || totalPages === 0}
                                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${currentPage === totalPages || totalPages === 0
                                    ? "bg-main-bg border border-main-border text-main-subfont cursor-not-allowed"
                                    : "bg-main-blue text-main-white hover:bg-main-blue/90"
                                    }`}
                            >
                                Next <FaArrowRight size={12} />
                            </button>
                        </div>

                        <p className="text-main-subfont md:absolute md:right-0 text-sm">
                            Page{' '}
                            <span className="px-3 py-1.5 bg-main-th border border-main-border text-main-font font-medium rounded-lg">
                                {currentPage}
                            </span>
                            {' '}of{' '}
                            <span className="font-medium text-main-font">{totalPages}</span>
                        </p>
                    </div>
                </div>
            </div>

            {/* Plan Details Modal */}
            {showModal && selectedPlan && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div ref={modalRef} className="bg-main-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden border border-main-border">
                        {/* Modal Header */}
                        <div className="bg-main-blue p-5 text-white relative">
                            <button
                                onClick={() => { setShowModal(false); setSelectedPlan(null); }}
                                className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-xl transition-colors"
                            >
                                <FaTimes size={16} />
                            </button>
                            <div className="flex items-center gap-3">
                                <div className="p-3 bg-white/20 rounded-xl">
                                    <FaMoneyBillWave size={24} />
                                </div>
                                <div>
                                    <p className="text-white/80 text-sm">Plan Purchase</p>
                                    <h2 className="text-xl font-bold">{selectedPlan?.p_name || 'N/A'}</h2>
                                </div>
                            </div>
                            <div className="mt-3">{getStatusBadge(selectedPlan?.status)}</div>
                        </div>

                        {/* Modal Body */}
                        <div className="p-5 space-y-4 max-h-[50vh] overflow-y-auto">
                            {/* Transaction ID with Copy */}
                            <div className="bg-main-th border border-main-blue/20 rounded-xl p-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-main-blue/10 rounded-lg">
                                            <FaReceipt className="text-main-blue" size={18} />
                                        </div>
                                        <div>
                                            <p className="text-xs text-main-blue font-medium uppercase tracking-wide">Transaction ID</p>
                                            <p className="font-mono text-sm text-main-font mt-0.5">{selectedPlan?.p_trn || 'N/A'}</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleCopyTrn(selectedPlan?.p_trn)}
                                        className="p-2.5 bg-main-blue/10 hover:bg-main-blue/20 rounded-lg transition-colors group"
                                        title="Copy Transaction ID"
                                    >
                                        <FaCopy className="text-main-blue group-hover:scale-110 transition-transform" size={16} />
                                    </button>
                                </div>
                            </div>

                            {/* User Card */}
                            <div
                                onClick={() => handleUserClick(selectedPlan?.u_id?._id)}
                                className="bg-main-th border border-main-blue/20 rounded-xl p-4 cursor-pointer hover:shadow-md transition-all group"
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2.5 bg-main-blue/10 rounded-full">
                                            <FaUser className="text-main-blue" size={18} />
                                        </div>
                                        <div>
                                            <p className="text-xs text-main-blue font-medium uppercase tracking-wide">User</p>
                                            <p className="font-semibold text-main-font group-hover:text-main-blue transition-colors">
                                                {selectedPlan?.u_id?.u_name || 'N/A'}
                                            </p>
                                            <p className="text-xs text-main-subfont mt-0.5">{selectedPlan?.u_id?.u_device || 'N/A'}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className={`px-2 py-1 text-xs rounded-lg ${selectedPlan?.u_id?.u_status ? 'bg-main-blue/10 text-main-blue' : 'bg-main-delete/10 text-main-delete'}`}>
                                            {selectedPlan?.u_id?.u_status ? 'Active' : 'Blocked'}
                                        </span>
                                        <FaExternalLinkAlt className="text-main-blue group-hover:translate-x-1 transition-transform" size={12} />
                                    </div>
                                </div>
                            </div>

                            {/* Pricing Grid */}
                            <div className="grid grid-cols-2 gap-3">
                                <div className="bg-main-th border border-main-blue/20 rounded-xl p-3 text-center">
                                    <p className="text-xs text-main-blue font-medium uppercase tracking-wide">Paid</p>
                                    <p className="text-2xl font-bold text-main-blue mt-1">₹{selectedPlan?.p_price}</p>
                                </div>
                                <div className="bg-main-bg border border-main-border rounded-xl p-3 text-center">
                                    <p className="text-xs text-main-subfont font-medium uppercase tracking-wide">Original</p>
                                    <p className="text-2xl font-bold text-main-subfont line-through mt-1">₹{selectedPlan?.p_main_price}</p>
                                </div>
                                <div className="bg-main-th border border-main-blue/20 rounded-xl p-3 text-center">
                                    <p className="text-xs text-main-blue font-medium uppercase tracking-wide">Saved</p>
                                    <p className="text-xl font-bold text-main-blue mt-1">₹{selectedPlan?.p_save_price}</p>
                                </div>
                                <div className="bg-main-yellow/10 border border-main-yellow/30 rounded-xl p-3 text-center">
                                    <p className="text-xs text-main-yellow font-medium uppercase tracking-wide">Discount</p>
                                    <p className="text-xl font-bold text-main-yellow mt-1">{selectedPlan?.p_discount}%</p>
                                </div>
                            </div>

                            {/* Duration & Dates */}
                            <div className="bg-main-bg border border-main-border rounded-xl p-4">
                                <div className="flex items-center gap-2 mb-3">
                                    <FaClock className="text-main-subfont" size={14} />
                                    <p className="text-xs text-main-subfont font-medium uppercase tracking-wide">Duration & Timeline</p>
                                </div>
                                <div className="flex items-center justify-center mb-3">
                                    <span className="px-4 py-2 bg-main-blue/10 text-main-blue font-bold rounded-full text-lg">
                                        {selectedPlan?.p_duration} Days
                                    </span>
                                </div>
                                <div className="grid grid-cols-3 gap-2 text-center text-sm">
                                    <div className="bg-main-white rounded-lg p-2 border border-main-border">
                                        <p className="text-[10px] text-main-subfont uppercase">Start</p>
                                        <p className="font-medium text-main-font text-xs mt-1">{formatDate(selectedPlan?.start_date)}</p>
                                    </div>
                                    <div className="bg-main-white rounded-lg p-2 border border-main-border">
                                        <p className="text-[10px] text-main-subfont uppercase">End</p>
                                        <p className="font-medium text-main-font text-xs mt-1">{formatDate(selectedPlan?.end_date)}</p>
                                    </div>
                                    <div className="bg-main-white rounded-lg p-2 border border-main-border">
                                        <p className="text-[10px] text-main-subfont uppercase">Created</p>
                                        <p className="font-medium text-main-font text-xs mt-1">{formatDate(selectedPlan?.createdAt)}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="p-4 bg-main-bg border-t border-main-border">
                            <div className="flex gap-3">
                                <button
                                    onClick={() => handleUserClick(selectedPlan?.u_id?._id)}
                                    className="flex-1 px-4 py-2.5 bg-main-blue text-white rounded-xl hover:bg-main-blue/90 transition-colors flex items-center justify-center gap-2 font-medium"
                                >
                                    <FaUser size={14} /> View User
                                </button>
                                <button
                                    onClick={() => {
                                        setShowModal(false);
                                        setSelectedPlan(null);
                                        handleDeleteSelected(selectedPlan._id);
                                    }}
                                    className="flex-1 px-4 py-2.5 bg-main-delete text-white rounded-xl hover:bg-main-delete/90 transition-colors flex items-center justify-center gap-2 font-medium"
                                >
                                    <FaTrash size={14} /> Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

};

export default PlanHistory;